import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Form, Formik } from 'formik';

import { DATA_TABLE_SOURCE_UTILITY_PARTNER_NOTES } from 'common/dataTableConstants';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import {
  SR_API_ACTION_UTILITY_PARTNER_NOTE_CREATE,
  SR_API_ACTION_UTILITY_PARTNER_NOTE_DELETE,
} from 'api/utilityActionTypes';

import SrCard from 'client/components/SrCard/SrCard';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrBox from 'client/components/SrBox';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrFormikField, { SR_FIELD_THEME_GREYCLIFF_DARK_BG } from 'client/components/SrFormikField';

import './SrUtilityProfitPartnerNotes.less';

const SrUtilityProfitPartnerNotes = ({ utilityPartnerId }) => {
  const dispatch = useDispatch();

  const onNoteSubmit = ({ note, reloadItems, resetForm }) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_NOTE_CREATE,
      payload: {
        utilityPartnerId,
        note,
      },
      onSuccess: (createdNote) => {
        resetForm();
        reloadItems({
          count: 1,
          filter: {
            'utilityPartnerNotes.id': createdNote.id,
          },
        });
      },
    });
  };

  const onNoteDelete = ({ noteId, reloadItems }) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_NOTE_DELETE,
      payload: {
        id: noteId,
      },
      onSuccess: () => {
        reloadItems({
          count: 1,
          filter: {
            'utilityPartnerNotes.id': noteId,
          },
        });
      },
    });
  };

  return (
    <SrDataTableConnected
      itemsPropName="notes"
      dataSource={DATA_TABLE_SOURCE_UTILITY_PARTNER_NOTES}
      initialViewSettings={{
        sort: {
          createdAt: 'desc',
        },
        filter: {
          'utilityPartnerNotes.utilityPartnerId': utilityPartnerId,
          'utilityPartnerNotes.deletedAt': null,
        },
      }}
    >
      {({ notes, reloadItems, dataTableApiState, onInfiniteScrollWrapperRef }) => (
        <>
          <SrCard padding="16px" borderRadius="14px" styleName="SrUtilityProfitPartnerNotes">
            <SrText font="greycliff" fontSize="18px" weight="800" display="block">
              Partner Notes
            </SrText>
            <SrBox>
              <Formik
                enableReinitialize
                initialValues={{
                  note: '',
                }}
                onSubmit={({ note }, { resetForm }) =>
                  onNoteSubmit({ note, reloadItems, resetForm })
                }
                render={(formikBag) => (
                  <Form>
                    <SrFormikField
                      theme={SR_FIELD_THEME_GREYCLIFF_DARK_BG}
                      formikBag={formikBag}
                      styleName="noteField"
                      formField={{
                        id: 'note',
                        validations: ['required'],
                        type: 'long_text',
                        properties: {
                          noMargin: true,
                          placeholder: 'Write note here...',
                          onKeyPress: (e) => {
                            if (e.which === 13) {
                              e.preventDefault();
                              formikBag.handleSubmit();
                            }
                          },
                        },
                      }}
                    />
                  </Form>
                )}
              />
            </SrBox>
            <SrBox
              marginTop="4px"
              maxHeight="350px"
              overflowY="auto"
              overflowX="hidden"
              ref={onInfiniteScrollWrapperRef}
            >
              {notes.map((note) => {
                const formattedDate = moment(note.createdAt).format('MMM D, YYYY [at] h:mma');
                return (
                  <SrBox key={note.id} marginTop="10px">
                    <SrText font="greycliff" fontSize="14px" weight="600" display="block">
                      {note.note}
                    </SrText>
                    <SrBox styleName="noteDetails">
                      <SrBox styleName="details">
                        <SrText font="greycliff" fontSize="12px" color="grey">
                          {note.authorName} | &nbsp;
                        </SrText>
                        <SrText font="greycliff" fontSize="12px" color="grey">
                          {formattedDate}
                        </SrText>
                      </SrBox>
                      <SrBox styleName="actions">
                        <SrText
                          font="greycliff"
                          fontSize="12px"
                          color="blue300"
                          cursor="pointer"
                          styleName="deleteNote"
                          onClick={() => onNoteDelete({ noteId: note.id, reloadItems })}
                        >
                          Delete Note
                        </SrText>
                      </SrBox>
                    </SrBox>
                  </SrBox>
                );
              })}
            </SrBox>
            {dataTableApiState.inProgress && (
              <SrFlex justify="center">
                <div className="loading-ellipses" />
              </SrFlex>
            )}
          </SrCard>
        </>
      )}
    </SrDataTableConnected>
  );
};

SrUtilityProfitPartnerNotes.propTypes = {
  utilityPartnerId: PropTypes.number.isRequired,
};

SrUtilityProfitPartnerNotes.displayName = 'SrUtilityProfitPartnerNotes';

export default SrUtilityProfitPartnerNotes;
